import './StudentItemDetail.scss';
import {
  ReportRows,
  ReportTable,
  RowCell,
  StudentItemDetailProps,
  StudentItemDetailResponse,
  UIReportTable,
  downloadStudentItemDetailReport,
  fetchStudentItemDetail,
} from './models';
import { useCallback, useEffect, useState } from 'react';
import { Admin, AdminStudent } from '../../shared';
import { genDDMenuItemLabel } from '../QueryPanel/models';
import { isType } from '../../shared';
import Spinner from '../Spinner/Spinner';
import { useSelector } from 'react-redux';
import { selectControls } from '../../redux/controls';
import gaEvents from '../../shared/analyticsEvents';
import NQTCheckBox from '../NQTCheckBox/NQTCheckBox';
import {
  CHECKBOX_GROUP_TYPES,
  ICheckBox,
} from '../NQTCheckBox/NQTCheckBox.models';
import { cloneDeep } from 'lodash';
import { ResultFragment } from '../StudentItem/ResultFragment';

const staticMenuStyle = {
  width: '100%',
};

export const StudentItemDetail = ({
  admin,
  students,
  fromQuiz,
  printSignal,
  exportSignal,
  setReportOnScreen,
}: StudentItemDetailProps): JSX.Element => {
  

  const controls = useSelector(selectControls);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [abortController, setAbortController] = useState<AbortController>();
  const [selectedStudent, setSelectedStudent] = useState<AdminStudent | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [studentItemDetail, setStudentItemDetail] = useState<ReportRows | null>(
    null
  );
  const [uiReportTable, setUIReportTable] = useState<UIReportTable | null>(
    null
  );
  const [noStudentItemDetail, setNoStudentItemDetail] =
    useState<boolean>(false);
  const [includeScoreGuides, setIncludeScoreGuides] = useState<boolean>(true);
  const [includeSampleResponses, setIncludeSampleResponses] =
    useState<boolean>(true);

  const ScoreGuideType: CHECKBOX_GROUP_TYPES = 'INCLUDE_SCORE_GUIDES';

  const scoreGuidCheckbox: ICheckBox = {
    isSelected: includeScoreGuides,
    label: controls?.Rpt_StudentItemDetail_ScoreGuidesInc?.controlText,
  };

  const sampleResponsesCheckbox: ICheckBox = {
    isSelected: includeSampleResponses,
    label: controls?.Rpt_StudentItemDetail_SampleResponsesInc?.controlText,
  };

  const onClickStudent = useCallback((student: AdminStudent) => {
    setSelectedStudent(student);
  }, []);

  const onClickCreate = useCallback(
    (admin: Admin, student: AdminStudent | null): void => {
      if (!student) {
        return;
      }

      const processStudentItemDetail = async () => {
        setIsLoading(true);
        const controller = new AbortController();
        setAbortController(controller);
        const response = await fetchStudentItemDetail(
          admin,
          student,
          fromQuiz,
          includeScoreGuides,
          includeSampleResponses,
          controller
        );
        if (
          isType<StudentItemDetailResponse>(
            response,
            'StudentItemDetailResponse'
          ) &&
          response.studentItemDetail.reportDataExists
        ) {
          // convert reportRows to ReportTable
          // need separate head and body objects for nested tags
          const uiReportTable: UIReportTable = { tables: [] };
          let addBlankRow = false;
          let prevRowIsHeader = false;
          for (let iReportRow = 0; iReportRow < response.studentItemDetail.reportRows.length; iReportRow++) {
            const reportRow = response.studentItemDetail.reportRows[iReportRow];
            let reportTable: ReportTable = { thead: undefined, tbody: [] };
            for (let iRow = 0; iRow < reportRow.rows.length; iRow++) {
              const row = reportRow.rows[iRow];
              if (row.rowType === 0) {
                if (reportTable.thead) {
                  uiReportTable.tables.push(cloneDeep(reportTable));
                }
                // create thead
                reportTable = { thead: row, tbody: [] };
                // add flag to add blank row once per header before row type of 2
                prevRowIsHeader = true;
              } else if (row.rowType === 1) {
                reportTable.tbody.push(row);
                if (prevRowIsHeader) {
                  addBlankRow = true;
                  prevRowIsHeader = false;
                }
                // create tbody
              } else if (row.rowType === 2) {
                // create tbody
                if (addBlankRow) {
                  const blankRow: RowCell = {
                    stringValue: '',
                    dataValue: -1,
                    rowSpan: 1,
                    colSpan: 5,
                    hasScoreSymbol: false,
                    hasHtml: false,
                  };
                  reportTable.tbody.push({ rowCells: [blankRow], rowType: 1 });
                  addBlankRow = false;
                }
                reportTable.tbody.push(row);
              }
              if (iRow === reportRow.rows.length - 1) {
                uiReportTable.tables.push(cloneDeep(reportTable));
              }
            }
          }
          setStudentItemDetail(response.studentItemDetail.reportRows[0]);
          setUIReportTable(uiReportTable);
          setNoStudentItemDetail(false);
        } else {
          setNoStudentItemDetail(true);
          setStudentItemDetail((state) => {
            if (state) {
              return null;
            } else {
              return state;
            }
          });
        }
        setIsLoading(false);
      };

      if (
        !studentItemDetail ||
        !(studentItemDetail.headerInfo['Student: '] === student.testTakerID)
      ) {
        processStudentItemDetail();
        gaEvents.onGA4CreateStudentReport();
      }
    },
    [fromQuiz, includeSampleResponses, includeScoreGuides, studentItemDetail]
  );

  useEffect(() => {
    if (printSignal && !isPrinting) {
      setIsPrinting(true);
      const printableDiv = document.getElementById('section-to-print');
      if (printableDiv) {
        window.print();
      }
      setIsPrinting(false);
    }
  }, [isPrinting, printSignal]);

  useEffect(() => {
    if (exportSignal && !isExporting && studentItemDetail !== null) {
      setIsExporting(true);
      downloadStudentItemDetailReport([studentItemDetail], exportSignal).then((result) => {
        setIsExporting(false);
        if (!result) {
          alert('Sorry, failed to download student item detail report. Please try again.');
        }
      }).catch((error) => {
        console.error('Failed to download student item detail report:', error);
        setIsExporting(false);
      });
    }
  }, [exportSignal, isExporting, studentItemDetail]);

  useEffect(() => {
    return () => {
      abortController?.abort();
    };
  }, [abortController]);

  // selects first student on mount
  useEffect(() => {
    setSelectedStudent(students[0]);
  }, [students]);

  // toggles print disabled flag
  useEffect(() => {
    if (studentItemDetail) {
      setReportOnScreen(true);
    } else {
      setReportOnScreen(false);
    }

    return () => {
      setReportOnScreen(false);
    };
  }, [setReportOnScreen, studentItemDetail]);

  // click create report automatically for students
  useEffect(()=>{
    if(fromQuiz){
      window.setTimeout(() => {
        const createReportButton = document.getElementById('nqt-btn-create-report') as HTMLButtonElement;
        createReportButton.click();
      }, 300);
    } 
  },[fromQuiz]);

  return (
    <section
      className="sid-container section-to-print"
      id="student-item-detail-section"
    >
      <div className="sid-first-row hide-section ">
        {isLoading && <Spinner size={50} />}
        {!isLoading && <div className="sid-full-width row pt-3">
          <div className="sid-student-container-detail col-2 align-self-end">
            <div className="sid--student-header w-100">
              {controls?.Rpt_StudentItem_Student?.controlText}
            </div>
            <div className="dropdown w-100">
              <button
                className="nqt-dropdown-btn btn dropdown-toggle"
                type="button"
                id="dropdownAdminStudents"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="dropdown-interior">
                  {selectedStudent ? selectedStudent.testTakerName : ''}
                </span>
                <span className="dropdown-arrow"></span>
              </button>
              <ul
                className="dropdown-menu"
                style={staticMenuStyle}
                aria-labelledby="dropdownAdminStudents"
              >
                {genDDMenuItemLabel('Select Student')}
                {students.length > 0 &&
                  students.map((student, ind) => (
                    <li key={`tr-rpt-${ind}`}>
                      <a
                        className={
                          'dropdown-item' +
                          (student.testTakerID === selectedStudent?.testTakerID
                            ? ' selected'
                            : '')
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          onClickStudent(student);
                        }}
                        href="#"
                      >
                        {student.testTakerName}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          <div className="sid-include-guide col-2 pb-2 align-self-end justify-self-end">
            <NQTCheckBox
              id={'nqt-sid--include-guide'}
              containerClass="dp-checkbox-container"
              checkBoxObj={scoreGuidCheckbox}
              label={scoreGuidCheckbox.label}
              type={ScoreGuideType}
              updateHandler={() =>
                setIncludeScoreGuides(scoreGuidCheckbox.isSelected)
              }
            />
          </div>
          <div className="sid-sample-resp col-2 pb-2  align-self-end justify-self-end">
            <NQTCheckBox
              id={'nqt-sid--include-sample-responses'}
              containerClass="dp-checkbox-container"
              checkBoxObj={sampleResponsesCheckbox}
              label={sampleResponsesCheckbox.label}
              type={ScoreGuideType}
              updateHandler={() =>
                setIncludeSampleResponses(sampleResponsesCheckbox.isSelected)
              }
            />
          </div>
          <div className="sid-create-btn-container col align-self-end justify-self-end">
            <button
              id="nqt-btn-create-report"
              className="nqt-btn sid-create-btn"
              // disabled={!selectedStudent}
              onClick={() => onClickCreate(admin, selectedStudent)}
            >
              {controls?.Rpt_CreateReport?.controlText}
            </button>
          </div>
        </div>}
      </div>
      {noStudentItemDetail && !isLoading && (
        <div className="sid-error-message">
          <p className="error-message">
            {controls?.Rpt_ReportNoData?.controlText}
          </p>
        </div>
      )}
      {studentItemDetail && (
        <div id="section-to-print">
          <div className="sid-second-row">
            <h4 className="sid-blue-header">
              {controls?.Rpt_StudentItemDetailLabel
                ? controls.Rpt_StudentItemDetailLabel.controlText
                : 'Student Item Detail Report'}
            </h4>
            <span className="sid-blue-header-container">
              <h4 className="sid-blue-header">
                {controls?.Rpt_Comparison_Admin?.controlText}
              </h4>
              <span className="sid-header-label">
                {studentItemDetail.headerInfo['Administration: ']}
              </span>
            </span>
            <span className="sid-blue-header-container">
              <h4 className="sid-blue-header">
                {controls?.Rpt_StudentItem_Roster?.controlText}
              </h4>
              <span className="sid-header-label">
                {studentItemDetail.headerInfo['Roster: ']}
              </span>
            </span>
            <span className="sid-blue-header-container">
              <h4 className="sid-blue-header">
                {controls?.Rpt_StudentItem_Student?.controlText}
              </h4>
              <span className="sid-header-label">
                {studentItemDetail.headerInfo['Student: ']}
              </span>
            </span>
            <span className="sid-blue-header-container">
              <h4 className="sid-blue-header">
                {controls?.Rpt_StudentItemDetail_ScoreGuidesHas?.controlText}
              </h4>
              <span className="sid-header-label">
                {studentItemDetail.headerInfo['Has Score Guides: ']}
              </span>
            </span>
            <span className="sid-blue-header-container">
              <h4 className="sid-blue-header">
                {controls?.Rpt_StudentItemDetail_SampleResponsesHas?.controlText}
              </h4>
              <span className="sid-header-label">
                {studentItemDetail.headerInfo['Has Sample Responses: ']}
              </span>
            </span>
          </div>
          <div className="table-responsive sid-table-container">
            {uiReportTable && uiReportTable.tables.length > 0 &&
                !isLoading &&
                uiReportTable.tables.map((tbl, idxh) => {
                  return (
                    <table className="table table-bordered sid-table" key={`table-key-${idxh}`}>
                      <thead id={`${idxh}-table-head`}>
                        <tr className="nqt-table-header-row">
                          {tbl.thead?.rowCells.map((cell, ind) => {
                            return (
                              <td
                                className="nqt-table-header-cell"
                                key={`table-header-${idxh}-${ind}`}
                                colSpan={cell.colSpan}
                                rowSpan={cell.rowSpan}
                              >
                                {cell.stringValue}
                              </td>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {tbl.tbody.map((drow, idxb) => {
                          return <tr
                            className={`nqt-table-body-row ${drow.rowType === 2 ? 'nqt-table-body-row-gray' : ''}`}
                            key={`table-body-key-${idxb}`}
                          >
                            {drow.rowCells.map((cell, ind) => (
                              cell.hasScoreSymbol ?
                                <td
                                  className="nqt-table-body-cell"
                                  key={`table-body-row-cell-${idxb}-${ind}`}
                                  colSpan={cell.colSpan}
                                  rowSpan={cell.rowSpan}

                                >
                                  <ResultFragment
                                    colIndex={ind}
                                    result={cell.stringValue}
                                  />
                                </td> : cell.hasHtml ?
                                  <td
                                    className="nqt-table-body-cell"
                                    key={`table-body-row-cell-${idxb}-${ind}`}
                                    colSpan={cell.colSpan}
                                    rowSpan={cell.rowSpan}
                                    dangerouslySetInnerHTML={ {__html: cell.stringValue} }
                                  >
                                  </td>
                                  :
                                  <td
                                    className="nqt-table-body-cell"
                                    key={`table-body-row-cell-${idxb}-${ind}`}
                                    colSpan={cell.colSpan}
                                    rowSpan={cell.rowSpan}
                                  >
                                    {cell.stringValue}
                                  </td>
                            ))}
                          </tr>;
                        })}
                      </tbody>
                    </table>
                  );
                }
                )}
          </div>
        </div>
      )}
    </section>
  );
};

export default StudentItemDetail;
